import React from 'react';
import format from 'date-fns/format';

import { IMultisportEvent, ISubscriberInfoFn, OddsMarketTypes, SDKDateOptions, SportTypes } from '../../../../../packages/types';
import { TWO_TEAMS_SPORTS, RACING_SPORTS } from '../../../../../packages/constants/sportGroups';
import { IMultisportEventsLabels } from '../../utils/getMultisportLabels';
import TennisEvent from '../sportEvents/TennisEvent';
import {
  convertTZ,
  getLabelValue,
  getDateFnsLocale,
  defaultHandleBettingMarketValueClick,
  defaultHandleEventClick,
} from '@sportal365/smp-widget-utils';
import TwoTeamsEvent from '../sportEvents/TwoTeamsEvent';
import RacingEvent from '../sportEvents/RacingEvent';

interface EventProps {
  sport: SportTypes;
  event: IMultisportEvent;
  topEventsSelected?: boolean;
  eventLink?: string;
  target?: string;
  sdkDateOptions: SDKDateOptions;
  labels: IMultisportEventsLabels;
  competitionLogo: string;
  displayOdds: boolean;
  handleClick: ISubscriberInfoFn;
}

const DATE_FORMAT = 'dd MMM';
const TIME_FORMAT = 'HH:mm';

const getEventDateTime = ({ date, sdkDateOptions }: { date: string | null; sdkDateOptions: SDKDateOptions }) => {
  if (date) {
    return {
      date: format(convertTZ(date, sdkDateOptions.dataConfigTimezone), DATE_FORMAT, {
        locale: getDateFnsLocale(sdkDateOptions.dataConfigLang),
      }),
      time: format(convertTZ(date, sdkDateOptions.dataConfigTimezone), TIME_FORMAT, {
        locale: getDateFnsLocale(sdkDateOptions.dataConfigLang),
      }),
    };
  }

  return {
    date: '',
    time: '',
  };
};

const SportEvent: React.FC<EventProps> = ({
  sport,
  event,
  topEventsSelected,
  target,
  eventLink,
  sdkDateOptions,
  labels,
  competitionLogo,
  displayOdds,
  handleClick,
}) => {
  const dateTime = getEventDateTime({ date: event.date, sdkDateOptions });

  const firstMarketSelections =
    event.odds && event.odds.length > 0 && event.odds[0].markets && event.odds[0].markets['1x2'] && event.odds[0].markets['1x2']?.selections
      ? event.odds[0].markets['1x2']?.selections
      : [];

  const secondMarketSelections =
    event.odds && event.odds.length > 0 && event.odds[0].markets && event.odds[0].markets['12'] && event.odds[0].markets['12']?.selections
      ? event.odds[0].markets['12']?.selections
      : [];

  const bookmaker = event.odds[0]?.bookmaker;
  const selectedMarket =
    sport === SportTypes.FOOTBALL
      ? (event.odds[0]?.markets['1x2']?.type?.name as OddsMarketTypes)
      : (event.odds[0]?.markets['12']?.type?.name as OddsMarketTypes);

  const handleOddCoefficientClick = (link: string) =>
    bookmaker && selectedMarket ? defaultHandleBettingMarketValueClick(handleClick, bookmaker, selectedMarket, link) : undefined;

  const handleEventClick = (event: string) => defaultHandleEventClick(handleClick, event);

  if (TWO_TEAMS_SPORTS.includes(sport) && event.sides) {
    const props = {
      dateTime,
      statusType: event.statusType,
      statusText: event.statusText,
      firstTeam: event.sides?.first,
      secondTeam: event.sides?.second,
      eventLink,
      urlTarget: target,
      competitionLogo,
      topEventsSelected,
      selections: sport === SportTypes.FOOTBALL ? firstMarketSelections : secondMarketSelections,
      displayOdds,
      handleClick: bookmaker && selectedMarket ? handleOddCoefficientClick : undefined,
      handleEventClick,
      id: event.id,
    };

    return <TwoTeamsEvent {...props} />;
  }

  if (RACING_SPORTS.includes(sport) && event.participants) {
    return (
      <RacingEvent
        stage={event.stage}
        dateTime={dateTime}
        participants={event.participants}
        winnerText={getLabelValue(labels.winner, 'Winner')}
        urlTarget={target}
        eventLink={eventLink}
        status={event.statusType}
        competitionLogo={competitionLogo}
        topEventsSelected={topEventsSelected}
        displayOdds={displayOdds}
        handleClick={handleClick}
        handleEventClick={handleEventClick}
        id={event.id}
      />
    );
  }

  if (sport === SportTypes.TENNIS && event.sides) {
    const props = {
      dateTime,
      statusType: event.statusType,
      statusText: event.statusText,
      playerOne: event.sides?.first,
      playerTwo: event.sides?.second,
      urlTarget: target,
      eventLink,
      competitionLogo,
      topEventsSelected,
      selections: secondMarketSelections,
      displayOdds,
      handleClick: bookmaker && selectedMarket ? handleOddCoefficientClick : undefined,
      handleEventClick,
      id: event.id,
    };

    return <TennisEvent {...props} />;
  }

  return null;
};

export default SportEvent;
